import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { rfqService, SubmitOrderInput, SubmitOfferInput, UpdateOfferInput, CounterOfferInput, AddUpdateInput, RequestExtensionInput } from '../../services/rfq.service';
import type { BuyerOrder, DesignerOffer, Project } from '../../types/commissions.types';

// ── State ────────────────────────────────────────────────────────────────

export interface RfqState {
  // Buyer orders
  orders: BuyerOrder[];
  ordersLoading: boolean;
  ordersError: string | null;
  ordersLoadedAt: number | null;

  // Buyer projects (active/completed projects for the signed-in buyer)
  buyerProjects: Project[];
  buyerProjectsLoading: boolean;
  buyerProjectsLoadedAt: number | null;

  // Per-id caches
  ordersById: Record<string, BuyerOrder>;
  offersByOrderId: Record<string, DesignerOffer[]>;
  orderDetailLoadedAt: Record<string, number>;
  projectsById: Record<string, Project>;
  projectLoadedAt: Record<string, number>;

  // Current order + its offers (detail view)
  currentOrder: BuyerOrder | null;
  currentOrderLoading: boolean;
  currentOrderError: string | null;
  currentOffers: DesignerOffer[];
  currentOffersLoading: boolean;

  // Designer feeds
  incomingBriefs: BuyerOrder[];
  incomingLoading: boolean;
  incomingLoadedAt: number | null;
  designerOffers: DesignerOffer[];
  designerOffersLoading: boolean;
  designerOffersLoadedAt: number | null;
  designerProjects: Project[];
  designerProjectsLoading: boolean;
  designerProjectsLoadedAt: number | null;

  // Current project (tracker / detail)
  currentProject: Project | null;
  currentProjectLoading: boolean;
  currentProjectError: string | null;

  // Submission state
  submitting: boolean;
  submitError: string | null;
}

// Cache TTL — list/detail fetches skip re-hitting the API within this window.
const CACHE_TTL_MS = 60_000;
const isFresh = (loadedAt: number | null | undefined) =>
  !!loadedAt && Date.now() - loadedAt < CACHE_TTL_MS;

type ForceArg = { force?: boolean } | undefined;

const initialState: RfqState = {
  orders: [],
  ordersLoading: false,
  ordersError: null,
  ordersLoadedAt: null,

  buyerProjects: [],
  buyerProjectsLoading: false,
  buyerProjectsLoadedAt: null,

  ordersById: {},
  offersByOrderId: {},
  orderDetailLoadedAt: {},
  projectsById: {},
  projectLoadedAt: {},

  currentOrder: null,
  currentOrderLoading: false,
  currentOrderError: null,
  currentOffers: [],
  currentOffersLoading: false,

  incomingBriefs: [],
  incomingLoading: false,
  incomingLoadedAt: null,
  designerOffers: [],
  designerOffersLoading: false,
  designerOffersLoadedAt: null,
  designerProjects: [],
  designerProjectsLoading: false,
  designerProjectsLoadedAt: null,

  currentProject: null,
  currentProjectLoading: false,
  currentProjectError: null,

  submitting: false,
  submitError: null,
};

type RootStateLike = { rfq: RfqState };

// ── Thunks ───────────────────────────────────────────────────────────────

export const submitOrder = createAsyncThunk(
  'rfq/submitOrder',
  async (input: SubmitOrderInput, { rejectWithValue }) => {
    try {
      return await rfqService.submitOrder(input);
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  }
);

export const fetchOrders = createAsyncThunk<
  BuyerOrder[], ForceArg, { state: RootStateLike }
>(
  'rfq/fetchOrders',
  async (_, { rejectWithValue }) => {
    try {
      return await rfqService.listOrders();
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  },
  {
    condition: (arg, { getState }) => {
      const s = getState().rfq;
      if (s.ordersLoading) return false;
      if (arg?.force) return true;
      return !isFresh(s.ordersLoadedAt);
    },
  }
);

export const fetchBuyerProjects = createAsyncThunk<
  Project[], ForceArg, { state: RootStateLike }
>(
  'rfq/fetchBuyerProjects',
  async (_, { rejectWithValue }) => {
    try {
      return await rfqService.listBuyerProjects();
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  },
  {
    condition: (arg, { getState }) => {
      const s = getState().rfq;
      if (s.buyerProjectsLoading) return false;
      if (arg?.force) return true;
      return !isFresh(s.buyerProjectsLoadedAt);
    },
  }
);

export const fetchOrder = createAsyncThunk<
  { order: BuyerOrder; offers: DesignerOffer[] },
  { orderId: string; force?: boolean },
  { state: RootStateLike }
>(
  'rfq/fetchOrder',
  async ({ orderId }, { rejectWithValue }) => {
    try {
      const [order, offers] = await Promise.all([
        rfqService.getOrder(orderId),
        rfqService.listOffers(orderId),
      ]);
      return { order, offers };
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  },
  {
    condition: ({ orderId, force }, { getState }) => {
      const s = getState().rfq;
      if (s.currentOrderLoading && s.currentOrder?.id === orderId) return false;
      if (force) return true;
      return !isFresh(s.orderDetailLoadedAt[orderId]);
    },
  }
);

export const acceptOffer = createAsyncThunk(
  'rfq/acceptOffer',
  async ({ orderId, offerId }: { orderId: string; offerId: string }, { rejectWithValue }) => {
    try {
      return await rfqService.acceptOffer(orderId, offerId);
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  }
);

export const makeOffer = createAsyncThunk(
  'rfq/makeOffer',
  async ({ offerId, input }: { offerId: string; input: CounterOfferInput }, { rejectWithValue }) => {
    try {
      return await rfqService.makeOffer(offerId, input);
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  }
);

export const fetchIncomingBriefs = createAsyncThunk<
  BuyerOrder[], ForceArg, { state: RootStateLike }
>(
  'rfq/fetchIncomingBriefs',
  async (_, { rejectWithValue }) => {
    try {
      return await rfqService.listIncomingBriefs();
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  },
  {
    condition: (arg, { getState }) => {
      const s = getState().rfq;
      if (s.incomingLoading) return false;
      if (arg?.force) return true;
      return !isFresh(s.incomingLoadedAt);
    },
  }
);

export const fetchDesignerOffers = createAsyncThunk<
  DesignerOffer[], ForceArg, { state: RootStateLike }
>(
  'rfq/fetchDesignerOffers',
  async (_, { rejectWithValue }) => {
    try {
      return await rfqService.listDesignerOffers();
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  },
  {
    condition: (arg, { getState }) => {
      const s = getState().rfq;
      if (s.designerOffersLoading) return false;
      if (arg?.force) return true;
      return !isFresh(s.designerOffersLoadedAt);
    },
  }
);

export const fetchDesignerProjects = createAsyncThunk<
  Project[], ForceArg, { state: RootStateLike }
>(
  'rfq/fetchDesignerProjects',
  async (_, { rejectWithValue }) => {
    try {
      return await rfqService.listDesignerProjects();
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  },
  {
    condition: (arg, { getState }) => {
      const s = getState().rfq;
      if (s.designerProjectsLoading) return false;
      if (arg?.force) return true;
      return !isFresh(s.designerProjectsLoadedAt);
    },
  }
);

export const submitOffer = createAsyncThunk(
  'rfq/submitOffer',
  async ({ orderId, input }: { orderId: string; input: SubmitOfferInput }, { rejectWithValue }) => {
    try {
      return await rfqService.submitOffer(orderId, input);
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  }
);

export const updateOffer = createAsyncThunk(
  'rfq/updateOffer',
  async ({ offerId, input }: { offerId: string; input: UpdateOfferInput }, { rejectWithValue }) => {
    try {
      return await rfqService.updateOffer(offerId, input);
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  }
);

export const withdrawOffer = createAsyncThunk(
  'rfq/withdrawOffer',
  async (offerId: string, { rejectWithValue }) => {
    try {
      return await rfqService.withdrawOffer(offerId);
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  }
);

export const declineOrder = createAsyncThunk(
  'rfq/declineOrder',
  async ({ orderId, reason }: { orderId: string; reason?: string }, { rejectWithValue }) => {
    try {
      await rfqService.declineOrder(orderId, reason);
      return orderId;
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  }
);

export const fetchProject = createAsyncThunk<
  Project,
  { projectId: string; force?: boolean },
  { state: RootStateLike }
>(
  'rfq/fetchProject',
  async ({ projectId }, { rejectWithValue }) => {
    try {
      return await rfqService.getProject(projectId);
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  },
  {
    condition: ({ projectId, force }, { getState }) => {
      const s = getState().rfq;
      if (s.currentProjectLoading && s.currentProject?.id === projectId) return false;
      if (force) return true;
      return !isFresh(s.projectLoadedAt[projectId]);
    },
  }
);

export const addProjectUpdate = createAsyncThunk(
  'rfq/addProjectUpdate',
  async ({ projectId, input }: { projectId: string; input: AddUpdateInput }, { rejectWithValue }) => {
    try {
      return await rfqService.addProjectUpdate(projectId, input);
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  }
);

export const markStages = createAsyncThunk(
  'rfq/markStages',
  async ({ projectId, doneStages }: { projectId: string; doneStages: string[] }, { rejectWithValue }) => {
    try {
      return await rfqService.markStages(projectId, doneStages);
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  }
);

export const markDelivered = createAsyncThunk(
  'rfq/markDelivered',
  async (projectId: string, { rejectWithValue }) => {
    try {
      return await rfqService.markDelivered(projectId);
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  }
);

export const confirmDelivery = createAsyncThunk(
  'rfq/confirmDelivery',
  async (projectId: string, { rejectWithValue }) => {
    try {
      return await rfqService.confirmDelivery(projectId);
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  }
);

export const requestExtension = createAsyncThunk(
  'rfq/requestExtension',
  async ({ projectId, input }: { projectId: string; input: RequestExtensionInput }, { rejectWithValue }) => {
    try {
      return await rfqService.requestExtension(projectId, input);
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  }
);

export const respondToExtension = createAsyncThunk(
  'rfq/respondToExtension',
  async ({ projectId, action }: { projectId: string; action: 'accept' | 'reject' }, { rejectWithValue }) => {
    try {
      return await rfqService.respondToExtension(projectId, action);
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  }
);

// ── Slice ────────────────────────────────────────────────────────────────

const rfqSlice = createSlice({
  name: 'rfq',
  initialState,
  reducers: {
    clearCurrentOrder(state) {
      state.currentOrder = null;
      state.currentOffers = [];
      state.currentOrderError = null;
    },
    clearCurrentProject(state) {
      state.currentProject = null;
      state.currentProjectError = null;
    },
    clearSubmitError(state) {
      state.submitError = null;
    },
    /** Invalidate list caches so the next fetch refetches from server. */
    invalidateOrdersCache(state) {
      state.ordersLoadedAt = null;
      state.buyerProjectsLoadedAt = null;
    },
    invalidateDesignerCache(state) {
      state.incomingLoadedAt = null;
      state.designerOffersLoadedAt = null;
      state.designerProjectsLoadedAt = null;
    },
    invalidateOrderDetail(state, action: PayloadAction<string>) {
      delete state.orderDetailLoadedAt[action.payload];
    },
    invalidateProject(state, action: PayloadAction<string>) {
      delete state.projectLoadedAt[action.payload];
    },
    /** Optimistic update — patch a single offer in currentOffers */
    patchCurrentOffer(state, action: PayloadAction<DesignerOffer>) {
      state.currentOffers = state.currentOffers.map(o =>
        o.id === action.payload.id ? action.payload : o
      );
    },
  },
  extraReducers: (builder) => {

    // ── submitOrder
    builder
      .addCase(submitOrder.pending, (state) => { state.submitting = true; state.submitError = null; })
      .addCase(submitOrder.fulfilled, (state, { payload }) => {
        state.submitting = false;
        state.orders.unshift(payload);
      })
      .addCase(submitOrder.rejected, (state, { payload }) => {
        state.submitting = false;
        state.submitError = payload as string;
      });

    // ── fetchOrders
    builder
      .addCase(fetchOrders.pending, (state) => { state.ordersLoading = true; state.ordersError = null; })
      .addCase(fetchOrders.fulfilled, (state, { payload }) => {
        state.ordersLoading = false;
        state.orders = payload;
        state.ordersLoadedAt = Date.now();
        payload.forEach(o => { state.ordersById[o.id] = o; });
      })
      .addCase(fetchOrders.rejected, (state, { payload }) => {
        state.ordersLoading = false;
        state.ordersError = payload as string;
      });

    // ── fetchOrder (order + offers)
    builder
      .addCase(fetchOrder.pending, (state) => {
        state.currentOrderLoading = true;
        state.currentOffersLoading = true;
        state.currentOrderError = null;
      })
      .addCase(fetchOrder.fulfilled, (state, { payload }) => {
        state.currentOrderLoading = false;
        state.currentOffersLoading = false;
        state.currentOrder = payload.order;
        state.currentOffers = payload.offers;
        state.ordersById[payload.order.id] = payload.order;
        state.offersByOrderId[payload.order.id] = payload.offers;
        state.orderDetailLoadedAt[payload.order.id] = Date.now();
      })
      .addCase(fetchOrder.rejected, (state, { payload }) => {
        state.currentOrderLoading = false;
        state.currentOffersLoading = false;
        state.currentOrderError = payload as string;
      });

    // ── acceptOffer → redirect handled by page (payment URL)
    builder
      .addCase(acceptOffer.pending, (state) => { state.submitting = true; })
      .addCase(acceptOffer.fulfilled, (state) => { state.submitting = false; })
      .addCase(acceptOffer.rejected, (state, { payload }) => {
        state.submitting = false;
        state.submitError = payload as string;
      });

    // ── makeOffer (counter)
    builder
      .addCase(makeOffer.fulfilled, (state, { payload }) => {
        state.currentOffers = state.currentOffers.map(o =>
          o.id === payload.id ? payload : o
        );
      });

    // ── fetchIncomingBriefs
    builder
      .addCase(fetchIncomingBriefs.pending, (state) => { state.incomingLoading = true; })
      .addCase(fetchIncomingBriefs.fulfilled, (state, { payload }) => {
        state.incomingLoading = false;
        state.incomingBriefs = payload;
        state.incomingLoadedAt = Date.now();
        payload.forEach(o => { state.ordersById[o.id] = o; });
      })
      .addCase(fetchIncomingBriefs.rejected, (state) => { state.incomingLoading = false; });

    // ── fetchDesignerOffers
    builder
      .addCase(fetchDesignerOffers.pending, (state) => { state.designerOffersLoading = true; })
      .addCase(fetchDesignerOffers.fulfilled, (state, { payload }) => {
        state.designerOffersLoading = false;
        state.designerOffers = payload;
        state.designerOffersLoadedAt = Date.now();
      })
      .addCase(fetchDesignerOffers.rejected, (state) => { state.designerOffersLoading = false; });

    // ── fetchDesignerProjects
    builder
      .addCase(fetchDesignerProjects.pending, (state) => { state.designerProjectsLoading = true; })
      .addCase(fetchDesignerProjects.fulfilled, (state, { payload }) => {
        state.designerProjectsLoading = false;
        state.designerProjects = payload;
        state.designerProjectsLoadedAt = Date.now();
        payload.forEach(p => { state.projectsById[p.id] = p; });
      })
      .addCase(fetchDesignerProjects.rejected, (state) => { state.designerProjectsLoading = false; });

    // ── submitOffer
    builder
      .addCase(submitOffer.pending, (state) => { state.submitting = true; state.submitError = null; })
      .addCase(submitOffer.fulfilled, (state, { payload }) => {
        state.submitting = false;
        state.designerOffers.unshift(payload);
        state.incomingBriefs = state.incomingBriefs.filter(b => b.id !== payload.orderId);
      })
      .addCase(submitOffer.rejected, (state, { payload }) => {
        state.submitting = false;
        state.submitError = payload as string;
      });

    // ── updateOffer
    builder
      .addCase(updateOffer.fulfilled, (state, { payload }) => {
        state.designerOffers = state.designerOffers.map(o =>
          o.id === payload.id ? payload : o
        );
      });

    // ── withdrawOffer
    builder
      .addCase(withdrawOffer.fulfilled, (state, { payload }) => {
        state.designerOffers = state.designerOffers.map(o =>
          o.id === payload.id ? payload : o
        );
      });

    // ── declineOrder
    builder
      .addCase(declineOrder.fulfilled, (state, { payload }) => {
        state.incomingBriefs = state.incomingBriefs.filter(b => b.id !== payload);
      });

    // ── fetchProject
    builder
      .addCase(fetchProject.pending, (state) => { state.currentProjectLoading = true; state.currentProjectError = null; })
      .addCase(fetchProject.fulfilled, (state, { payload }) => {
        state.currentProjectLoading = false;
        state.currentProject = payload;
        state.projectsById[payload.id] = payload;
        state.projectLoadedAt[payload.id] = Date.now();
      })
      .addCase(fetchProject.rejected, (state, { payload }) => {
        state.currentProjectLoading = false;
        state.currentProjectError = payload as string;
      });

    // ── all project mutation thunks patch currentProject and designerProjects
    const patchProject = (state: RfqState, { payload }: PayloadAction<Project>) => {
      state.submitting = false;
      state.currentProject = payload;
      state.projectsById[payload.id] = payload;
      state.projectLoadedAt[payload.id] = Date.now();
      state.designerProjects = state.designerProjects.map(p =>
        p.id === payload.id ? payload : p
      );
      state.buyerProjects = state.buyerProjects.map(p =>
        p.id === payload.id ? payload : p
      );
    };

    // ── fetchBuyerProjects
    builder
      .addCase(fetchBuyerProjects.pending, (state) => { state.buyerProjectsLoading = true; })
      .addCase(fetchBuyerProjects.fulfilled, (state, { payload }) => {
        state.buyerProjectsLoading = false;
        state.buyerProjects = payload;
        state.buyerProjectsLoadedAt = Date.now();
        payload.forEach(p => { state.projectsById[p.id] = p; });
      })
      .addCase(fetchBuyerProjects.rejected, (state) => { state.buyerProjectsLoading = false; });

    builder
      .addCase(addProjectUpdate.pending, (state) => { state.submitting = true; })
      .addCase(addProjectUpdate.fulfilled, patchProject)
      .addCase(addProjectUpdate.rejected, (state) => { state.submitting = false; });

    builder
      .addCase(markStages.pending, (state) => { state.submitting = true; })
      .addCase(markStages.fulfilled, patchProject)
      .addCase(markStages.rejected, (state) => { state.submitting = false; });

    builder
      .addCase(markDelivered.pending, (state) => { state.submitting = true; })
      .addCase(markDelivered.fulfilled, patchProject)
      .addCase(markDelivered.rejected, (state) => { state.submitting = false; });

    builder
      .addCase(confirmDelivery.pending, (state) => { state.submitting = true; })
      .addCase(confirmDelivery.fulfilled, patchProject)
      .addCase(confirmDelivery.rejected, (state) => { state.submitting = false; });

    builder
      .addCase(requestExtension.pending, (state) => { state.submitting = true; })
      .addCase(requestExtension.fulfilled, patchProject)
      .addCase(requestExtension.rejected, (state) => { state.submitting = false; });

    builder
      .addCase(respondToExtension.pending, (state) => { state.submitting = true; })
      .addCase(respondToExtension.fulfilled, patchProject)
      .addCase(respondToExtension.rejected, (state) => { state.submitting = false; });
  },
});

export const {
  clearCurrentOrder,
  clearCurrentProject,
  clearSubmitError,
  patchCurrentOffer,
  invalidateOrdersCache,
  invalidateDesignerCache,
  invalidateOrderDetail,
  invalidateProject,
} = rfqSlice.actions;
export default rfqSlice.reducer;
